import { useMemo, useState } from "react";
import { Box, Typography, CircularProgress, Grid, Checkbox } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import style from "./testType.module.css";
import {
  useGetTestTypeDetailQuery,
  useGetSpecialitiesQuery,
  useUpdateTestTypeDetailsMutation,
} from "../../services/api";
import { Button, LabelBox } from "../../components";
import { ConfirmationDialog } from "../../components/Dialog";
import { colors } from "../../theme/colors";
import { mapSpecialityIdsToItems } from "./testTypeUtils";
import type { Speciality } from "../../types/index";

/**
 * Renders the Test Type details view.
 *
 * The view fetches test type details and related specialities, and displays
 * read-only metadata including deactivation information when applicable.
 *
 * Returns:
 *   JSX.Element: The Test Type Details page content.
 */
const TestTypeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activateModalVisible, setActivateModalVisible] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const {
    data: testTypeDetails,
    isLoading,
    isFetching,
  } = useGetTestTypeDetailQuery(id ?? "", { skip: !id });

  const { data: specialityData = { items: [] }, isLoading: isSpecialitiesLoading } = useGetSpecialitiesQuery({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: false,
    search_key: ""
  });
  const [updateTestTypeDetails] = useUpdateTestTypeDetailsMutation();

  const specialities = useMemo<Speciality[]>(
    () => mapSpecialityIdsToItems(testTypeDetails?.speciality_ids, specialityData?.items),
    [specialityData, testTypeDetails]
  );

  const handleActivate = async () => {
    if (!testTypeDetails?.id) {
      return;
    }
    try {
      setIsActivating(true);
      await updateTestTypeDetails({
        is_active: true,
        id: testTypeDetails.id,
        deactivate_reason: "",
      }).unwrap();
      setActivateModalVisible(false);
    } catch (error: any) {
      setActivateModalVisible(false);
      setMessage({
        type: "error",
        text: error?.message || error?.data?.detail || "Something went wrong!",
      });
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "30px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          height: "100%",
        }}
      >
        {isLoading || isFetching || isSpecialitiesLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 97px)",
            }}
          >
            <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", padding: "15px 40px" }}>
            <Typography className={style.testTypeDetailHeading}>
              Test Type Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Name</Typography>
                <Typography className={style.value}>
                  {testTypeDetails?.name}
                </Typography>
              </Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Info</Typography>
                <Typography className={style.value}>
                  {testTypeDetails?.info}
                </Typography>
              </Box>
            </Box>
            <Box className={style.detailsRow}>
              <Typography className={style.title}>Storage Type</Typography>
              <Typography className={style.value}>
                {testTypeDetails?.specimen_storage}
              </Typography>
            </Box>
            <Box
              className={style.detailsRow}
              sx={{ alignItems: "flex-start !important" }}
            >
              <Typography className={style.title}>Specialities</Typography>
              <Box className={style.value}>
                <Grid container columnSpacing={3}>
                  {specialities?.map((speciality: Speciality) => {
                    return (
                      <Grid
                        item
                        xs={specialities?.length > 10 ? 6 : 12}
                        key={speciality?.id}
                      >
                        <Box
                          display={"flex"}
                          alignItems={"center"}
                          paddingY={1}
                        >
                          <Checkbox
                            checked={true}
                            disableTouchRipple
                            sx={{
                              transform: "scale(0.92)",
                              padding: "0px",
                            }}
                            disabled
                          />
                          <Typography sx={{ marginLeft: 1 }}>
                            {speciality?.name ?? ""}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
            <Box className={style.detailsRow}>
              <Typography className={style.title}>Intra Operative Test</Typography>
              <Checkbox
                checked={testTypeDetails?.is_intra_operative}
                disableTouchRipple
                sx={{
                  transform: "scale(0.92)",
                  padding: "0px",
                  marginLeft: "7px",
                  marginTop: "1px"
                }}
                disabled
              />
            </Box>
            {testTypeDetails?.deactivate_reason && (
              <Box>
                <Typography className={style.testTypeDetailHeading}>
                  Deactivation Details
                </Typography>
                <Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Reason for deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {testTypeDetails?.deactivate_reason}
                    </Typography>
                  </Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Date of Deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {testTypeDetails?.deactivated_at
                        ? dayjs(testTypeDetails?.deactivated_at).format(
                          "MMMM DD YYYY, hh:mm:ss A"
                        )
                        : "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {message && (
              <Box mt={2}>
                <LabelBox
                  boxProps={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                  data={message}
                  onClose={() => setMessage(null)}
                />
              </Box>
            )}
            <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
              <Button
                sx={{
                  width: "200px",
                  height: "55px",
                  ":hover": { backgroundColor: "transparent" },
                }}
                variant="outlined"
                onClick={() => navigate(-1)}
                label={"Close"}
              />
              {testTypeDetails?.is_active ? (
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: colors.primary },
                  }}
                  variant="contained"
                  label={"Edit"}
                  onClick={() =>
                    navigate(`/test-types/edit/${testTypeDetails?.id}`)
                  }
                />
              ) : (
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: colors.primary },
                  }}
                  variant="contained"
                  label={"Activate"}
                  onClick={() => setActivateModalVisible(true)}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
      {activateModalVisible && (
        <ConfirmationDialog
          visible={activateModalVisible}
          message={`Are you sure you want to activate "${testTypeDetails?.name}"?`}
          onClose={() => setActivateModalVisible(false)}
          onConfirm={handleActivate}
          loading={isActivating}
        />
      )}
    </Box >
  );
}

export default TestTypeDetails;
